/**
 * Headless state machine for conversational data capture.
 *
 * Instead of rendering a form, the bot asks for one field at a time as chat
 * messages and the visitor answers from the normal chat input. This module
 * owns the sequencing and validation; rendering, socket traffic and message
 * bubbles stay in the widget.
 *
 * Answers are buffered here and only handed over once every field is
 * resolved — the caller then forwards them through the same
 * `visitor_details` path the pre-chat form already uses. Nothing is sent
 * mid-way, so a visitor who abandons halfway creates no partial contact.
 *
 * The state is immutable: every transition returns a new object, so it drops
 * straight into `useState` without extra copying.
 */

import type { PreChatFormField } from '../client/api-types';
import { validateField } from '../lib/field-validation';

export interface CaptureState {
  readonly fields: readonly PreChatFormField[];
  /** Index of the field being asked. Equals `fields.length` once finished. */
  readonly index: number;
  /** Answers accepted so far, trimmed. Skipped fields are absent. */
  readonly answers: Readonly<Record<string, string>>;
  /**
   * Configured closing line, sent once every field is resolved so the visitor
   * knows the questionnaire is over and they can start asking. Empty = say
   * nothing and hand the input straight back to the chat.
   */
  readonly completionMessage: string;
}

/**
 * What the caller should do after a transition.
 *
 * - `asking`: show `messages` and keep the input open for the same or next
 *   field. On a rejected answer `messages` holds the error followed by the
 *   question again, so the visitor never has to scroll back to see what was
 *   asked.
 * - `complete`: show `messages` — the configured closing line, if any — and
 *   submit `answers`.
 */
export type CaptureResult =
  | { status: 'asking'; state: CaptureState; messages: string[] }
  | {
      status: 'complete';
      state: CaptureState;
      messages: string[];
      answers: Record<string, string>;
    };

/** The bot message used to ask for a field. Falls back to the field label. */
export function questionFor(field: PreChatFormField): string {
  const question = field.question?.trim();
  return question || field.label;
}

/** Create the initial state for a capture run. */
export function createCaptureState(
  fields: readonly PreChatFormField[],
  completionMessage?: string,
): CaptureState {
  return {
    fields,
    index: 0,
    answers: {},
    completionMessage: completionMessage?.trim() || '',
  };
}

/** The field currently being asked, or `null` once the run is finished. */
export function currentField(state: CaptureState): PreChatFormField | null {
  return state.fields[state.index] ?? null;
}

export function isDone(state: CaptureState): boolean {
  return state.index >= state.fields.length;
}

/** Whether the visitor is allowed to skip the field being asked. */
export function canSkipCurrent(state: CaptureState): boolean {
  const field = currentField(state);
  return field !== null && !field.required;
}

/**
 * Kick off a run: returns the first question, or completes immediately when
 * there is nothing to ask.
 */
export function start(state: CaptureState): CaptureResult {
  return settle(state, []);
}

/**
 * Feed the visitor's answer to the current field.
 *
 * A rejected answer leaves `index` untouched, so the same field is asked
 * again. An accepted answer is stored trimmed and the run moves on.
 */
export function submitAnswer(
  state: CaptureState,
  rawAnswer: string,
): CaptureResult {
  const field = currentField(state);
  if (!field) {
    return settle(state, []);
  }

  const error = validateField(field, rawAnswer);
  if (error) {
    return { status: 'asking', state, messages: [error, questionFor(field)] };
  }

  const value = rawAnswer.trim();
  const next: CaptureState = {
    ...state,
    index: state.index + 1,
    // An accepted-but-empty answer only happens on an optional field. Keep it
    // out of the buffer so the payload matches what the pre-chat form sends.
    answers: value ? { ...state.answers, [field.name]: value } : state.answers,
  };

  return settle(next, []);
}

/**
 * Skip the current field without answering. Only valid on optional fields —
 * on a required one this behaves like an empty answer and re-asks.
 */
export function skipCurrent(state: CaptureState): CaptureResult {
  const field = currentField(state);
  if (!field) {
    return settle(state, []);
  }
  if (field.required) {
    return submitAnswer(state, '');
  }
  return settle({ ...state, index: state.index + 1 }, []);
}

/**
 * Resolve a state into the next thing the bot says: the upcoming question, or
 * completion when every field is resolved.
 */
function settle(state: CaptureState, messages: string[]): CaptureResult {
  const field = currentField(state);

  if (!field) {
    return {
      status: 'complete',
      state,
      messages: state.completionMessage
        ? [...messages, state.completionMessage]
        : messages,
      answers: { ...state.answers },
    };
  }

  return {
    status: 'asking',
    state,
    messages: [...messages, questionFor(field)],
  };
}
